<template>
  <div class="vehicle-bigdata-page">
    <!-- 顶部统计 -->
    <div class="top-stats">
      <div class="stat-card">
        <div class="stat-label">车辆总数</div>
        <div class="stat-value">{{ summary.total }}</div>
      </div>
      <div class="stat-card">
        <div class="stat-label">在线车辆</div>
        <div class="stat-value success">{{ summary.online }}</div>
      </div>
      <div class="stat-card">
        <div class="stat-label">清运中</div>
        <div class="stat-value success">{{ summary.working }}</div>
      </div>
      <div class="stat-card">
        <div class="stat-label">空闲车辆</div>
        <div class="stat-value danger">{{ summary.idle }}</div>
      </div>
      <div class="stat-card">
        <div class="stat-label">总速度均值</div>
        <div class="stat-value">{{ summary.avgSpeed }} <span>km/h</span></div>
      </div>
      <div class="stat-card">
        <div class="stat-label">当前清运总重量</div>
        <div class="stat-value">{{ summary.totalCurrentWeight }} <span>kg</span></div>
      </div>
    </div>

    <!-- 查询区 -->
    <div class="query-bar">
      <div class="query-left">
        <el-input
          v-model.trim="keyword"
          clearable
          placeholder="搜索车牌 / 车辆名 / 司机 / IMEI"
          class="query-input"
          @keyup.enter.native="handleSearch"
        />
        <el-button type="primary" icon="el-icon-search" @click="handleSearch">查询</el-button>
        <el-button icon="el-icon-refresh" @click="handleReset">重置</el-button>
      </div>

      <div class="query-right">
        <span class="refresh-tip">数据每 {{ refreshSeconds }} 秒自动刷新</span>
        <el-switch
          v-model="autoRefresh"
          active-text="自动刷新"
          inactive-text="已关闭"
          @change="handleAutoRefreshChange"
        />
      </div>
    </div>

    <!-- 主体 -->
    <div class="dashboard-grid">
      <!-- 左侧：地图+列表 -->
      <div class="grid-card left-panel">
        <div class="card-header">
          <div class="card-title">
            <span class="title-dot"></span>
            车辆定位分布
          </div>
          <div class="card-actions">
            <span class="sub-info">在线 {{ summary.online }} / 总数 {{ vehicleList.length }}</span>
            <i class="el-icon-full-screen action-icon" @click="openFullscreen"></i>
          </div>
        </div>

        <div class="left-panel-body">
          <div class="map-wrap">
            <div ref="miniMap" class="mini-map"></div>

            <div class="map-legend">
              <span><i class="legend-dot red"></i> 空闲</span>
              <span><i class="legend-dot green"></i> 清运中</span>
              <span><i class="legend-dot gray"></i> 离线/异常</span>
            </div>
          </div>

          <div class="vehicle-list-wrap">
            <div class="list-summary">
              <div class="summary-box">
                <div class="summary-name">空闲</div>
                <div class="summary-num danger">{{ summary.idle }}</div>
              </div>
              <div class="summary-box">
                <div class="summary-name">清运中</div>
                <div class="summary-num success">{{ summary.working }}</div>
              </div>
            </div>

            <div class="vehicle-list">
              <div
                v-for="item in previewVehicleList"
                :key="item.vehicle_id"
                class="vehicle-item"
                :class="{ active: activeVehicle && activeVehicle.vehicle_id === item.vehicle_id }"
                @click="handleSelectVehicle(item)"
              >
                <div class="vehicle-item-top">
                  <span class="status-tag" :class="getStatusClass(item)"></span>
                  <span class="vehicle-name" :title="item.vehicle_name">{{ item.vehicle_name || '-' }}</span>
                  <span class="vehicle-state-text" :class="getStatusClass(item)">
                    {{ getWorkStatusText(item) }}
                  </span>
                </div>

                <div class="vehicle-item-bottom">
                  <span>{{ item.plate_no || '-' }}</span>
                  <span>{{ item.driver_name || '-' }}</span>
                </div>

                <div class="vehicle-item-bottom second">
                  <span>{{ item.imei || '-' }}</span>
                  <span>{{ formatSpeed(item.speed) }}</span>
                </div>
              </div>

              <div v-if="!previewVehicleList.length" class="empty-text">
                暂无车辆数据
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- 右上：状态统计 -->
      <div class="grid-card middle-top-panel">
        <div class="card-header light">
          <div class="card-title dark">
            <span class="title-dot"></span>
            车辆状态统计
          </div>
        </div>
        <div class="chart-card-body">
          <div class="status-pie-wrap">
            <div class="pie-ring">
              <div class="pie-center">
                <div class="pie-total">{{ summary.total }}</div>
                <div class="pie-label">车辆总数</div>
              </div>
            </div>

            <div class="status-legend-list">
              <div class="legend-row">
                <span class="legend-left"><i class="legend-dot blue"></i> 在线</span>
                <span class="legend-right">{{ summary.online }}</span>
              </div>
              <div class="legend-row">
                <span class="legend-left"><i class="legend-dot red"></i> 空闲</span>
                <span class="legend-right">{{ summary.idle }}</span>
              </div>
              <div class="legend-row">
                <span class="legend-left"><i class="legend-dot green"></i> 清运中</span>
                <span class="legend-right">{{ summary.working }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- 右下：TOP5 -->
      <div class="grid-card middle-bottom-panel">
        <div class="card-header light">
          <div class="card-title dark">
            <span class="title-dot"></span>
            当前清运重量 TOP5
          </div>
        </div>
        <div class="chart-card-body">
          <div v-if="weightTopList.length" class="top5-list">
            <div v-for="(item, index) in weightTopList" :key="item.vehicle_id" class="top5-item">
              <div class="top5-head">
                <span class="rank-num">{{ index + 1 }}</span>
                <span class="top5-name">{{ item.vehicle_name || item.plate_no || '-' }}</span>
                <span class="top5-value">{{ safeWeight(item.current_room_weight) }} kg</span>
              </div>
              <div class="top5-bar">
                <div
                  class="top5-bar-inner"
                  :style="{ width: getWeightBarWidth(item.current_room_weight) }"
                ></div>
              </div>
            </div>
          </div>
          <div v-else class="empty-text">暂无清运重量数据</div>
        </div>
      </div>
    </div>

    <!-- 全屏地图 -->
    <el-dialog
      :visible.sync="fullscreenVisible"
      custom-class="vehicle-full-dialog"
      :append-to-body="true"
      :close-on-click-modal="false"
      :show-close="false"
      fullscreen
      @opened="initFullscreenMap"
    >
      <div class="fullscreen-panel">
        <div class="fullscreen-header">
          <div class="card-title">
            <span class="title-dot"></span>
            车辆定位追踪 · 全屏模式
          </div>

          <div class="fullscreen-actions">
            <el-button size="mini" icon="el-icon-refresh" @click="fetchVehicleList">刷新</el-button>
            <el-button size="mini" type="primary" icon="el-icon-close" @click="fullscreenVisible = false">
              关闭
            </el-button>
          </div>
        </div>

        <div class="fullscreen-body">
          <!-- 左侧列表 -->
          <div class="fullscreen-left">
            <div class="fullscreen-block">
              <div class="block-title dark2">车辆列表</div>

              <div class="search-box">
                <el-input
                  v-model.trim="keyword"
                  size="small"
                  placeholder="输入车牌 / 名称 / 司机 / IMEI"
                  clearable
                  @keyup.enter.native="handleSearch"
                />
              </div>

              <div class="fullscreen-list">
                <div
                  v-for="item in filteredVehicleList"
                  :key="item.vehicle_id"
                  class="fullscreen-device-item"
                  :class="{ active: activeVehicle && activeVehicle.vehicle_id === item.vehicle_id }"
                  @click="handleSelectVehicle(item, true)"
                >
                  <div class="fd-top">
                    <span class="status-tag" :class="getStatusClass(item)"></span>
                    <span class="fd-name" :title="item.vehicle_name">{{ item.vehicle_name || '-' }}</span>
                  </div>
                  <div class="fd-bottom">
                    <span>{{ item.plate_no || '-' }}</span>
                    <span>{{ getWorkStatusText(item) }}</span>
                  </div>
                  <div class="fd-bottom">
                    <span>{{ item.driver_name || '-' }}</span>
                    <span>{{ formatSpeed(item.speed) }}</span>
                  </div>
                </div>

                <div v-if="!filteredVehicleList.length" class="empty-text">
                  没有匹配到车辆
                </div>
              </div>
            </div>
          </div>

          <!-- 中间地图 -->
          <div class="fullscreen-center">
            <div ref="fullscreenMap" class="fullscreen-map"></div>
          </div>

          <!-- 右侧详情 -->
          <div class="fullscreen-right">
            <div class="fullscreen-block">
              <div class="block-title dark2">当前车辆简要信息</div>

              <div v-if="detailLoading" class="dark-loading">
                <i class="el-icon-loading"></i>
                <span>详情加载中...</span>
              </div>

              <div v-else-if="vehicleDetail" class="device-detail">
                <div class="detail-row">
                  <span>车辆名称</span>
                  <strong>{{ vehicleDetail.vehicle_name || '-' }}</strong>
                </div>
                <div class="detail-row">
                  <span>车牌号</span>
                  <strong>{{ vehicleDetail.plate_no || '-' }}</strong>
                </div>
                <div class="detail-row">
                  <span>司机</span>
                  <strong>{{ vehicleDetail.driver_name || '-' }}</strong>
                </div>
                <div class="detail-row">
                  <span>司机手机号</span>
                  <strong>{{ vehicleDetail.driver_mobile || '-' }}</strong>
                </div>
                <div class="detail-row">
                  <span>IMEI</span>
                  <strong>{{ vehicleDetail.imei || '-' }}</strong>
                </div>
                <div class="detail-row">
                  <span>状态</span>
                  <strong>{{ getWorkStatusText(vehicleDetail) }}</strong>
                </div>
                <div class="detail-row">
                  <span>速度</span>
                  <strong>{{ formatSpeed(vehicleDetail.speed) }}</strong>
                </div>
                <div class="detail-row">
                  <span>方向</span>
                  <strong>{{ vehicleDetail.course || 0 }}°</strong>
                </div>
                <div class="detail-row">
                  <span>最后定位时间</span>
                  <strong>{{ vehicleDetail.last_location_time || '-' }}</strong>
                </div>
                <div class="detail-row">
                  <span>最后数据时间</span>
                  <strong>{{ vehicleDetail.last_data_time || '-' }}</strong>
                </div>
                <div class="detail-row">
                  <span>纬度</span>
                  <strong>{{ vehicleDetail.lat || '-' }}</strong>
                </div>
                <div class="detail-row">
                  <span>经度</span>
                  <strong>{{ vehicleDetail.lng || '-' }}</strong>
                </div>
                <div class="detail-row">
                  <span>设备分组</span>
                  <strong>{{ vehicleDetail.group_name || '-' }}</strong>
                </div>
                <div class="detail-row">
                  <span>心跳ACC</span>
                  <strong>{{ vehicleDetail.heart_beat_acc === 1 ? '开' : '关' }}</strong>
                </div>
                <div class="detail-row">
                  <span>里程</span>
                  <strong>{{ vehicleDetail.last_mileage || 0 }}</strong>
                </div>
                <div class="detail-row">
                  <span>当前清运次数</span>
                  <strong>{{ vehicleDetail.current_collect_count || 0 }}</strong>
                </div>
                <div class="detail-row">
                  <span>当前清运总重量</span>
                  <strong>{{ safeWeight(vehicleDetail.current_room_weight) }} kg</strong>
                </div>
                <div class="detail-row">
                  <span>开始时间</span>
                  <strong>{{ formatTimeValue(vehicleDetail.current_start_time) }}</strong>
                </div>
                <div class="detail-row">
                  <span>结束时间</span>
                  <strong>{{ formatTimeValue(vehicleDetail.current_end_time) }}</strong>
                </div>
                <div class="detail-row">
                  <span>当前记录号</span>
                  <strong>{{ vehicleDetail.current_record_no || '-' }}</strong>
                </div>

                <div class="dark-community-block">
                  <div class="dark-community-title">当前正在拉的小区</div>
                  <div v-if="communityList.length" class="dark-community-list">
                    <div v-for="(item, index) in communityList" :key="index" class="dark-community-item">
                      <span>{{ item.community_name || '未知小区' }}</span>
                      <span>{{ safeWeight(item.room_weight || item.weight) }} kg</span>
                    </div>
                  </div>
                  <div v-else class="empty-detail">暂无小区清运数据</div>
                </div>

                <div class="dark-community-block">
                  <div class="dark-community-title">当前清运记录</div>
                  <div v-if="currentRecords.length" class="dark-community-list">
                    <div v-for="(item, index) in currentRecords" :key="index" class="dark-community-item">
                      <span>{{ item.record_no || item.code || item.record_id || '未知记录' }}</span>
                      <span>{{ item.code || '-' }}</span>
                    </div>
                  </div>
                  <div v-else class="empty-detail">暂无当前清运记录</div>
                </div>
              </div>

              <div v-else class="empty-detail">
                请从左侧选择一辆车
              </div>
            </div>
          </div>
        </div>
      </div>
    </el-dialog>
  </div>
</template>

<script>
/* global BMap */
import request from '@/utils/request'
import redcarImg from '@/assets/redcar.png'
import greencarImg from '@/assets/greencar.png'

export default {
  name: 'Dashboard',
  data() {
    return {
      officeId: 1750,
      keyword: '',
      autoRefresh: true,
      refreshSeconds: 30,
      refreshTimer: null,

      fullscreenVisible: false,

      miniMap: null,
      fullscreenMap: null,
      miniMarkers: [],
      fullscreenMarkers: [],
      miniInfoWindow: null,
      fullscreenInfoWindow: null,

      defaultPoint: { lng: 82.06633457829247, lat: 44.883308749787815 },

      vehicleList: [],
      activeVehicle: null,
      vehicleDetail: null,
      detailLoading: false,
      listLoading: false
    }
  },

  computed: {
    previewVehicleList() {
      return this.vehicleList.slice(0, 10)
    },

    filteredVehicleList() {
      const keyword = (this.keyword || '').toLowerCase()
      if (!keyword) return this.vehicleList
      return this.vehicleList.filter(item => {
        return [
          item.vehicle_name,
          item.plate_no,
          item.driver_name,
          item.imei
        ].some(v => String(v || '').toLowerCase().includes(keyword))
      })
    },

    summary() {
      const list = this.vehicleList || []
      const total = list.length
      const online = list.filter(item => this.isOnline(item)).length
      const working = list.filter(item => this.isWorking(item)).length
      const idle = list.filter(item => !this.isWorking(item)).length

      let totalSpeed = 0
      let speedCount = 0
      let totalCurrentWeight = 0

      list.forEach(item => {
        const speed = Number(item.speed || 0)
        if (!Number.isNaN(speed)) {
          totalSpeed += speed
          speedCount++
        }
        totalCurrentWeight += Number(item.current_room_weight || 0)
      })

      return {
        total,
        online,
        working,
        idle,
        avgSpeed: speedCount ? (totalSpeed / speedCount).toFixed(1) : '0.0',
        totalCurrentWeight: totalCurrentWeight.toFixed(1)
      }
    },

    weightTopList() {
      return [...this.vehicleList]
        .filter(item => Number(item.current_room_weight || 0) > 0)
        .sort((a, b) => Number(b.current_room_weight || 0) - Number(a.current_room_weight || 0))
        .slice(0, 5)
    },

    communityList() {
      if (!this.vehicleDetail) return []
      return this.vehicleDetail.communities || []
    },

    currentRecords() {
      if (!this.vehicleDetail) return []
      return this.vehicleDetail.current_records || []
    }
  },

  mounted() {
    this.initMiniMap()
    this.fetchVehicleList()
    this.startAutoRefresh()
    window.addEventListener('resize', this.handleResize)
  },

  beforeDestroy() {
    this.clearAutoRefresh()
    window.removeEventListener('resize', this.handleResize)
  },

  methods: {
    async fetchVehicleList() {
      this.listLoading = true
      try {
        const res = await request({
          url: '/wms/vehicle_gps/list',
          method: 'get',
          params: {
            office_id: this.officeId,
            keyword: this.keyword || undefined
          }
        })

        const rawList = this.extractVehicleList(res)
        const list = rawList.map(this.normalizeVehicleItem)

        this.vehicleList = list.filter(item => item && item.lat && item.lng)

        if (this.vehicleList.length) {
          if (this.activeVehicle && this.activeVehicle.vehicle_id) {
            const matched = this.vehicleList.find(
              v => String(v.vehicle_id) === String(this.activeVehicle.vehicle_id)
            )
            this.activeVehicle = matched || this.vehicleList[0]
          } else {
            this.activeVehicle = this.vehicleList[0]
          }

          if (this.activeVehicle && this.activeVehicle.vehicle_id) {
            this.fetchVehicleDetail(this.activeVehicle.vehicle_id, false)
          }
        } else {
          this.activeVehicle = null
          this.vehicleDetail = null
        }

        this.renderMiniMapMarkers()

        if (this.fullscreenVisible) {
          this.$nextTick(() => {
            this.renderFullscreenMapMarkers()
          })
        }
      } catch (error) {
        console.error('获取车辆列表失败', error)
        this.$message.error('获取车辆列表失败')
      } finally {
        this.listLoading = false
      }
    },

    async fetchVehicleDetail(vehicleId, showLoading = true) {
      if (!vehicleId) return
      if (showLoading) this.detailLoading = true

      try {
        const res = await request({
          url: `/wms/vehicle_gps/detail/${vehicleId}`,
          method: 'get'
        })

        const detailRaw = this.extractDetailData(res)
        const detail = this.normalizeVehicleItem(detailRaw || {})
        this.vehicleDetail = detail

        if (this.activeVehicle && String(this.activeVehicle.vehicle_id) === String(vehicleId)) {
          this.activeVehicle = Object.assign({}, this.activeVehicle, detail)
        }
      } catch (error) {
        console.error('获取车辆详情失败', error)
        this.$message.error('获取车辆详情失败')
      } finally {
        this.detailLoading = false
      }
    },

    extractVehicleList(res) {
      const data = res && res.data ? res.data : res

      if (Array.isArray(data)) {
        if (data.length && data[0] && Array.isArray(data[0].items)) {
          return data[0].items
        }
        return data
      }

      if (data && Array.isArray(data.items)) return data.items
      if (data && data.data && Array.isArray(data.data.items)) return data.data.items
      if (data && data.data && Array.isArray(data.data)) return data.data
      if (data && Array.isArray(data.list)) return data.list

      return []
    },

    extractDetailData(res) {
      const data = res && res.data ? res.data : res
      if (data && data.data) return data.data
      return data
    },

    normalizeVehicleItem(item) {
      const location = item.location || {}
      const raw = location.raw || {}
      const workInfo = item.work_info || {}

      const lat = this.pickNumber([
        item.lat,
        location.lat,
        raw.lastLat,
        raw.lat
      ])

      const lng = this.pickNumber([
        item.lng,
        location.lng,
        raw.lastLon,
        raw.lng
      ])

      const speed = this.pickNumber([
        item.speed,
        location.speed,
        raw.lastSpeed,
        raw.speed
      ], 0)

      const course = this.pickNumber([
        item.course,
        location.course,
        raw.lastCourse,
        raw.course
      ], 0)

      return {
        ...item,
        vehicle_id: item.vehicle_id,
        vehicle_name: item.vehicle_name || raw.deviceName || '-',
        plate_no: item.plate_no || raw.deviceName || '-',
        driver_name: item.driver_name || '-',
        driver_mobile: item.driver_mobile || '-',
        imei: item.imei || raw.imei || '-',

        lat,
        lng,
        speed,
        course,

        last_location_time: item.last_location_time || location.last_location_time || raw.lastLocationTime || '-',
        last_data_time: item.last_data_time || location.last_data_time || raw.lastDatTime || '-',

        group_name: raw.groupName || '-',
        heart_beat_acc: Number(raw.heartBeatAcc || 0),
        last_mileage: raw.lastMileage || 0,
        gps_status: raw.status,
        gps_status_text: item.status_text || location.status_text || '',

        work_status: item.work_status || workInfo.work_status || '空闲',
        work_status_code: item.work_status_code || workInfo.work_status_code || 0,

        current_collect_count: Number(
          item.current_collect_count != null
            ? item.current_collect_count
            : (workInfo.current_collect_count || 0)
        ),

        current_room_weight: Number(
          item.current_room_weight != null
            ? item.current_room_weight
            : (workInfo.current_room_weight || 0)
        ),

        current_start_time: item.current_start_time || workInfo.current_start_time || '',
        current_end_time: item.current_end_time || workInfo.current_end_time || '',
        current_record_no: item.current_record_no || workInfo.current_record_no || '',

        communities: Array.isArray(item.communities)
          ? item.communities
          : (Array.isArray(workInfo.communities) ? workInfo.communities : []),

        current_records: Array.isArray(item.current_records) ? item.current_records : [],
        record_nos: Array.isArray(workInfo.record_nos) ? workInfo.record_nos : [],

        raw_location: raw,
        raw_work_info: workInfo
      }
    },

    pickNumber(arr, defaultValue = null) {
      for (let i = 0; i < arr.length; i++) {
        const val = arr[i]
        if (val !== null && val !== undefined && val !== '') {
          const num = Number(val)
          if (!Number.isNaN(num)) return num
        }
      }
      return defaultValue
    },

    handleSearch() {
      this.fetchVehicleList()
    },

    handleReset() {
      this.keyword = ''
      this.fetchVehicleList()
    },

    handleSelectVehicle(item, inFullscreen = false) {
      if (!item || !item.lat || !item.lng) return

      this.activeVehicle = item
      this.fetchVehicleDetail(item.vehicle_id)

      const point = new BMap.Point(Number(item.lng), Number(item.lat))

      if (this.miniMap) {
        this.miniMap.panTo(point)
        this.miniMap.setZoom(15)
        this.openInfoWindow(this.miniMap, point, item, 'mini')
      }

      if (inFullscreen && this.fullscreenMap) {
        this.fullscreenMap.panTo(point)
        this.fullscreenMap.setZoom(16)
        this.openInfoWindow(this.fullscreenMap, point, item, 'fullscreen')
      }
    },

    initMiniMap() {
      if (!this.$refs.miniMap) return

      this.miniMap = new BMap.Map(this.$refs.miniMap)
      const point = new BMap.Point(this.defaultPoint.lng, this.defaultPoint.lat)
      this.miniMap.centerAndZoom(point, 12)
      this.miniMap.enableScrollWheelZoom(true)
      this.miniMap.setMapStyleV2({
        styleJson: this.getMapStyle()
      })
    },

    initFullscreenMap() {
      this.$nextTick(() => {
        if (!this.$refs.fullscreenMap) return

        if (!this.fullscreenMap) {
          this.fullscreenMap = new BMap.Map(this.$refs.fullscreenMap)
          const point = new BMap.Point(this.defaultPoint.lng, this.defaultPoint.lat)
          this.fullscreenMap.centerAndZoom(point, 12)
          this.fullscreenMap.enableScrollWheelZoom(true)
          this.fullscreenMap.setMapStyleV2({
            styleJson: this.getMapStyle()
          })
        }

        this.renderFullscreenMapMarkers()
      })
    },

    renderMiniMapMarkers() {
      if (!this.miniMap) return
      this.clearMapOverlays(this.miniMap, 'mini')

      const points = []

      this.vehicleList.forEach(item => {
        if (!item.lng || !item.lat) return

        const point = new BMap.Point(Number(item.lng), Number(item.lat))
        points.push(point)

        const marker = this.createMarker(item, point)
        this.miniMap.addOverlay(marker)
        this.miniMarkers.push(marker)

        marker.addEventListener('click', () => {
          this.activeVehicle = item
          this.fetchVehicleDetail(item.vehicle_id, false)
          this.openInfoWindow(this.miniMap, point, item, 'mini')
        })
      })

      if (points.length > 1) {
        this.miniMap.setViewport(points)
      } else if (points.length === 1) {
        this.miniMap.centerAndZoom(points[0], 15)
      }
    },

    renderFullscreenMapMarkers() {
      if (!this.fullscreenMap) return
      this.clearMapOverlays(this.fullscreenMap, 'fullscreen')

      const points = []

      this.vehicleList.forEach(item => {
        if (!item.lng || !item.lat) return

        const point = new BMap.Point(Number(item.lng), Number(item.lat))
        points.push(point)

        const marker = this.createMarker(item, point)
        this.fullscreenMap.addOverlay(marker)
        this.fullscreenMarkers.push(marker)

        marker.addEventListener('click', () => {
          this.activeVehicle = item
          this.fetchVehicleDetail(item.vehicle_id, false)
          this.openInfoWindow(this.fullscreenMap, point, item, 'fullscreen')
        })
      })

      if (points.length > 1) {
        this.fullscreenMap.setViewport(points)
      } else if (points.length === 1) {
        this.fullscreenMap.centerAndZoom(points[0], 15)
      }

      if (this.activeVehicle && this.activeVehicle.lng && this.activeVehicle.lat) {
        const point = new BMap.Point(Number(this.activeVehicle.lng), Number(this.activeVehicle.lat))
        this.fullscreenMap.panTo(point)
      }
    },

    createMarker(item, point) {
      // 红色车 = 空闲
      // 绿色车 = 清运中
      const iconUrl = this.isWorking(item) ? greencarImg : redcarImg

      const icon = new BMap.Icon(iconUrl, new BMap.Size(42, 42), {
        anchor: new BMap.Size(21, 21),
        imageSize: new BMap.Size(42, 42)
      })

      const marker = new BMap.Marker(point, { icon })
      marker.setRotation(Number(item.course) || 0)
      return marker
    },

    openInfoWindow(mapInstance, point, item, type) {
      const content = `
        <div style="min-width:260px;max-width:320px;font-size:12px;line-height:1.8;color:#1f2937;">
          <div style="font-size:14px;font-weight:700;margin-bottom:8px;color:#111827;">
            ${item.vehicle_name || '-'}
          </div>
          <div><strong>车牌号：</strong>${item.plate_no || '-'}</div>
          <div><strong>司机：</strong>${item.driver_name || '-'}</div>
          <div><strong>IMEI：</strong>${item.imei || '-'}</div>
          <div><strong>状态：</strong>${this.getWorkStatusText(item)}</div>
          <div><strong>速度：</strong>${this.formatSpeed(item.speed)}</div>
          <div><strong>方向：</strong>${item.course || 0}°</div>
          <div><strong>定位时间：</strong>${item.last_location_time || '-'}</div>
          <div><strong>数据更新时间：</strong>${item.last_data_time || '-'}</div>
        </div>
      `

      const infoWindow = new BMap.InfoWindow(content, {
        width: 300,
        title: ''
      })

      mapInstance.openInfoWindow(infoWindow, point)

      if (type === 'mini') this.miniInfoWindow = infoWindow
      if (type === 'fullscreen') this.fullscreenInfoWindow = infoWindow
    },

    clearMapOverlays(mapInstance, type) {
      mapInstance.clearOverlays()
      if (type === 'mini') {
        this.miniMarkers = []
      } else {
        this.fullscreenMarkers = []
      }
    },

    openFullscreen() {
      this.fullscreenVisible = true
      if (this.activeVehicle && this.activeVehicle.vehicle_id) {
        this.fetchVehicleDetail(this.activeVehicle.vehicle_id, false)
      }
    },

    isWorking(item) {
      const status = String(item.work_status || '')
      return status.indexOf('清运中') > -1
    },

    isOnline(item) {
      return !!(item.lat && item.lng)
    },

    getWorkStatusText(item) {
      return item.work_status || '空闲'
    },

    getStatusClass(item) {
      if (this.isWorking(item)) return 'green'
      if (this.isOnline(item)) return 'red'
      return 'gray'
    },

    formatSpeed(speed) {
      const n = Number(speed || 0)
      if (Number.isNaN(n)) return '0 km/h'
      return `${n} km/h`
    },

    safeWeight(v) {
      const n = Number(v || 0)
      if (Number.isNaN(n)) return '0.0'
      return n.toFixed(1)
    },

    formatTimeValue(v) {
      if (!v || v === 0 || v === '0') return '-'
      if (/^\d+$/.test(String(v))) {
        const date = new Date(Number(v) * 1000)
        if (Number.isNaN(date.getTime())) return String(v)
        const y = date.getFullYear()
        const m = String(date.getMonth() + 1).padStart(2, '0')
        const d = String(date.getDate()).padStart(2, '0')
        const h = String(date.getHours()).padStart(2, '0')
        const i = String(date.getMinutes()).padStart(2, '0')
        const s = String(date.getSeconds()).padStart(2, '0')
        return `${y}-${m}-${d} ${h}:${i}:${s}`
      }
      return String(v)
    },

    getWeightBarWidth(weight) {
      const max = this.weightTopList.length
        ? Math.max(...this.weightTopList.map(v => Number(v.current_room_weight || 0)))
        : 0
      if (!max) return '0%'
      const percent = (Number(weight || 0) / max) * 100
      return `${Math.max(percent, 6)}%`
    },

    handleResize() {
      if (this.fullscreenMap) {
        setTimeout(() => {
          this.fullscreenMap.centerAndZoom(
            new BMap.Point(
              this.activeVehicle && this.activeVehicle.lng ? this.activeVehicle.lng : this.defaultPoint.lng,
              this.activeVehicle && this.activeVehicle.lat ? this.activeVehicle.lat : this.defaultPoint.lat
            ),
            12
          )
        }, 200)
      }
    },

    startAutoRefresh() {
      this.clearAutoRefresh()
      if (!this.autoRefresh) return

      this.refreshTimer = setInterval(() => {
        this.fetchVehicleList()
      }, this.refreshSeconds * 1000)
    },

    clearAutoRefresh() {
      if (this.refreshTimer) {
        clearInterval(this.refreshTimer)
        this.refreshTimer = null
      }
    },

    handleAutoRefreshChange() {
      if (this.autoRefresh) {
        this.startAutoRefresh()
      } else {
        this.clearAutoRefresh()
      }
    },

    getMapStyle() {
      return [
        { featureType: 'water', elementType: 'all', stylers: { color: '#0f172a' } },
        { featureType: 'land', elementType: 'all', stylers: { color: '#111827' } },
        { featureType: 'highway', elementType: 'geometry', stylers: { color: '#1f2937' } },
        { featureType: 'arterial', elementType: 'geometry', stylers: { color: '#1e293b' } },
        { featureType: 'poi', elementType: 'all', stylers: { visibility: 'off' } },
        { featureType: 'green', elementType: 'all', stylers: { color: '#0b1220' } },
        { featureType: 'subway', elementType: 'all', stylers: { visibility: 'off' } },
        { featureType: 'railway', elementType: 'all', stylers: { visibility: 'off' } },
        { featureType: 'building', elementType: 'all', stylers: { color: '#172033' } },
        { featureType: 'label', elementType: 'labels.text.fill', stylers: { color: '#94a3b8' } },
        { featureType: 'label', elementType: 'labels.text.stroke', stylers: { color: '#111827' } }
      ]
    }
  }
}
</script>

<style scoped>
.vehicle-bigdata-page {
  min-height: 100%;
  background: #f3f6fb;
  padding: 12px;
  box-sizing: border-box;
}

.top-stats {
  display: grid;
  grid-template-columns: repeat(6, 1fr);
  gap: 12px;
  margin-bottom: 12px;
}

.stat-card {
  position: relative;
  overflow: hidden;
  padding: 18px 18px;
  border-radius: 14px;
  background: linear-gradient(135deg, #0f172a 0%, #18243c 100%);
  box-shadow: 0 8px 24px rgba(15, 23, 42, 0.16);
  color: #fff;
}

.stat-card::after {
  content: '';
  position: absolute;
  right: -20px;
  top: -20px;
  width: 90px;
  height: 90px;
  background: rgba(59, 130, 246, 0.12);
  border-radius: 50%;
}

.stat-label {
  font-size: 13px;
  color: rgba(255,255,255,0.75);
  margin-bottom: 8px;
}

.stat-value {
  font-size: 26px;
  font-weight: 700;
  letter-spacing: 1px;
}

.stat-value span {
  font-size: 13px;
  font-weight: 400;
}

.stat-value.success {
  color: #34d399;
}

.stat-value.danger {
  color: #f87171;
}

.query-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
  padding: 14px 16px;
  border-radius: 16px;
  background: #fff;
  border: 1px solid #e9eef7;
  box-shadow: 0 8px 24px rgba(15, 23, 42, 0.05);
}

.query-left {
  display: flex;
  align-items: center;
  gap: 10px;
}

.query-right {
  display: flex;
  align-items: center;
  gap: 12px;
}

.query-input {
  width: 300px;
}

.refresh-tip {
  color: #64748b;
  font-size: 13px;
}

.dashboard-grid {
  display: grid;
  grid-template-columns: 1.8fr 1fr;
  grid-template-rows: 340px 340px;
  gap: 12px;
}

.grid-card {
  border-radius: 16px;
  background: linear-gradient(180deg, #ffffff 0%, #f8fbff 100%);
  box-shadow: 0 8px 24px rgba(15, 23, 42, 0.06);
  border: 1px solid #e9eef7;
  overflow: hidden;
  position: relative;
}

.left-panel {
  grid-row: span 2;
  display: flex;
  flex-direction: column;
}

.middle-top-panel,
.middle-bottom-panel {
  display: flex;
  flex-direction: column;
}

.card-header {
  height: 52px;
  padding: 0 16px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: linear-gradient(90deg, #0f172a 0%, #172554 100%);
  color: #fff;
}

.card-header.light {
  background: linear-gradient(180deg, #ffffff 0%, #f8fbff 100%);
  border-bottom: 1px solid #edf2f7;
}

.card-title {
  display: flex;
  align-items: center;
  font-size: 16px;
  font-weight: 700;
}

.card-title.dark {
  color: #0f172a;
}

.title-dot {
  width: 8px;
  height: 8px;
  background: #38bdf8;
  border-radius: 50%;
  box-shadow: 0 0 10px rgba(56, 189, 248, 0.9);
  margin-right: 10px;
}

.card-actions {
  display: flex;
  align-items: center;
  gap: 12px;
}

.sub-info {
  font-size: 12px;
  color: rgba(255,255,255,0.75);
}

.action-icon {
  font-size: 18px;
  cursor: pointer;
  transition: all 0.2s;
}

.action-icon:hover {
  transform: scale(1.08);
  color: #67e8f9;
}

.left-panel-body {
  flex: 1;
  min-height: 0;
  display: grid;
  grid-template-columns: 1.5fr 0.95fr;
  gap: 12px;
  padding: 12px;
  background: #f7faff;
}

.map-wrap {
  position: relative;
  border-radius: 14px;
  overflow: hidden;
  background: #0f172a;
  border: 1px solid #d9e3f5;
}

.mini-map {
  width: 100%;
  height: 100%;
  min-height: 620px;
}

.map-legend {
  position: absolute;
  left: 12px;
  bottom: 12px;
  display: flex;
  gap: 16px;
  padding: 8px 12px;
  background: rgba(15, 23, 42, 0.72);
  border: 1px solid rgba(255,255,255,0.1);
  border-radius: 10px;
  color: #e5edf8;
  font-size: 12px;
  backdrop-filter: blur(4px);
}

.legend-dot {
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  margin-right: 6px;
}

.legend-dot.red {
  background: #ef4444;
}

.legend-dot.green {
  background: #22c55e;
}

.legend-dot.gray {
  background: #94a3b8;
}

.legend-dot.blue {
  background: #5b7cff;
}

.vehicle-list-wrap {
  display: flex;
  flex-direction: column;
  gap: 12px;
  min-height: 0;
}

.list-summary {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
}

.summary-box {
  background: #fff;
  border: 1px solid #e8eef8;
  border-radius: 12px;
  padding: 14px 12px;
  box-shadow: 0 4px 12px rgba(15, 23, 42, 0.04);
}

.summary-name {
  font-size: 13px;
  color: #64748b;
  margin-bottom: 6px;
}

.summary-num {
  font-size: 24px;
  font-weight: 700;
  color: #0f172a;
}

.summary-num.success {
  color: #10b981;
}

.summary-num.danger {
  color: #ef4444;
}

.vehicle-list {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  background: #fff;
  border: 1px solid #e8eef8;
  border-radius: 12px;
  padding: 10px;
}

.vehicle-item {
  padding: 12px 10px;
  border-radius: 10px;
  cursor: pointer;
  transition: all 0.2s;
  border: 1px solid transparent;
  margin-bottom: 8px;
}

.vehicle-item:hover {
  background: #f6faff;
  border-color: #dbeafe;
}

.vehicle-item.active {
  background: linear-gradient(90deg, #eff6ff 0%, #f0f9ff 100%);
  border-color: #93c5fd;
}

.vehicle-item-top,
.fd-top {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 6px;
}

.vehicle-name,
.fd-name {
  flex: 1;
  min-width: 0;
  font-size: 14px;
  color: #0f172a;
  font-weight: 600;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.vehicle-state-text {
  font-size: 12px;
  padding: 2px 8px;
  border-radius: 10px;
  background: #f1f5f9;
  color: #475569;
}

.vehicle-state-text.red {
  background: rgba(239, 68, 68, 0.12);
  color: #dc2626;
}

.vehicle-state-text.green {
  background: rgba(16, 185, 129, 0.12);
  color: #059669;
}

.vehicle-state-text.gray {
  background: rgba(148, 163, 184, 0.14);
  color: #64748b;
}

.vehicle-item-bottom,
.fd-bottom {
  display: flex;
  justify-content: space-between;
  gap: 10px;
  font-size: 12px;
  color: #64748b;
  margin-bottom: 4px;
}

.vehicle-item-bottom.second {
  margin-bottom: 0;
}

.status-tag {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  flex-shrink: 0;
}

.status-tag.red {
  background: #ef4444;
  box-shadow: 0 0 8px rgba(239, 68, 68, 0.6);
}

.status-tag.green {
  background: #22c55e;
  box-shadow: 0 0 8px rgba(34, 197, 94, 0.6);
}

.status-tag.gray {
  background: #94a3b8;
  box-shadow: 0 0 8px rgba(148, 163, 184, 0.5);
}

.chart-card-body {
  flex: 1;
  min-height: 0;
  padding: 12px;
}

.status-pie-wrap {
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
}

.pie-ring {
  width: 180px;
  height: 180px;
  margin: 0 auto 18px;
  border-radius: 50%;
  background: conic-gradient(
    #5b7cff 0% 33%,
    #ef4444 33% 66%,
    #22c55e 66% 100%
  );
  position: relative;
}

.pie-ring::after {
  content: '';
  position: absolute;
  left: 50%;
  top: 50%;
  width: 96px;
  height: 96px;
  transform: translate(-50%, -50%);
  border-radius: 50%;
  background: #fff;
}

.pie-center {
  position: absolute;
  z-index: 2;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  text-align: center;
}

.pie-total {
  font-size: 24px;
  font-weight: 700;
  color: #0f172a;
}

.pie-label {
  font-size: 12px;
  color: #64748b;
}

.status-legend-list {
  width: 100%;
  max-width: 240px;
  margin: 0 auto;
}

.legend-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 0;
  color: #475569;
  border-bottom: 1px dashed #e5e7eb;
}

.legend-left {
  display: flex;
  align-items: center;
}

.legend-right {
  font-weight: 700;
  color: #0f172a;
}

.top5-list {
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding-top: 6px;
}

.top5-item {
  width: 100%;
}

.top5-head {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 8px;
}

.rank-num {
  width: 22px;
  height: 22px;
  line-height: 22px;
  text-align: center;
  border-radius: 50%;
  background: #1d4ed8;
  color: #fff;
  font-size: 12px;
  font-weight: 700;
}

.top5-name {
  flex: 1;
  font-size: 13px;
  color: #0f172a;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.top5-value {
  font-size: 13px;
  color: #2563eb;
  font-weight: 700;
}

.top5-bar {
  width: 100%;
  height: 10px;
  border-radius: 6px;
  background: #e9eef7;
  overflow: hidden;
}

.top5-bar-inner {
  height: 100%;
  border-radius: 6px;
  background: linear-gradient(90deg, #60a5fa 0%, #2563eb 100%);
}

.empty-text,
.empty-detail {
  color: #94a3b8;
  font-size: 13px;
  text-align: center;
  padding: 30px 10px;
}

.fullscreen-panel {
  width: 100%;
  height: 100vh;
  background: #0b1220;
  display: flex;
  flex-direction: column;
}

.fullscreen-header {
  height: 60px;
  padding: 0 18px;
  background: linear-gradient(90deg, #0f172a 0%, #172554 100%);
  display: flex;
  align-items: center;
  justify-content: space-between;
  color: #fff;
  border-bottom: 1px solid rgba(255,255,255,0.08);
}

.fullscreen-actions {
  display: flex;
  gap: 10px;
}

.fullscreen-body {
  flex: 1;
  min-height: 0;
  display: grid;
  grid-template-columns: 300px 1fr 340px;
  gap: 12px;
  padding: 12px;
  box-sizing: border-box;
}

.fullscreen-left,
.fullscreen-right,
.fullscreen-center {
  min-height: 0;
}

.fullscreen-block {
  width: 100%;
  height: 100%;
  border-radius: 16px;
  background: linear-gradient(180deg, #111827 0%, #0f172a 100%);
  border: 1px solid rgba(148, 163, 184, 0.14);
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.block-title.dark2 {
  padding: 14px 16px;
  font-size: 15px;
  font-weight: 700;
  color: #e5edf8;
  background: transparent;
  border-bottom: 1px solid rgba(148, 163, 184, 0.12);
}

.search-box {
  padding: 12px;
}

.fullscreen-list {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: 0 12px 12px;
}

.fullscreen-device-item {
  background: rgba(255,255,255,0.03);
  border: 1px solid transparent;
  border-radius: 12px;
  padding: 12px;
  cursor: pointer;
  margin-bottom: 10px;
  transition: all 0.2s;
}

.fullscreen-device-item:hover {
  background: rgba(59, 130, 246, 0.08);
  border-color: rgba(96, 165, 250, 0.3);
}

.fullscreen-device-item.active {
  background: rgba(59, 130, 246, 0.12);
  border-color: rgba(96, 165, 250, 0.55);
}

.fd-name {
  color: #f8fafc;
}

.fd-bottom {
  color: #94a3b8;
}

.fullscreen-map {
  width: 100%;
  height: 100%;
  border-radius: 16px;
  overflow: hidden;
}

.device-detail {
  padding: 10px 16px 16px;
  overflow-y: auto;
}

.fullscreen-right .detail-row {
  display: flex;
  justify-content: space-between;
  gap: 16px;
  padding: 12px 0;
  border-bottom: 1px dashed rgba(148, 163, 184, 0.16);
}

.fullscreen-right .detail-row span {
  color: #94a3b8;
  font-size: 13px;
}

.fullscreen-right .detail-row strong {
  color: #f8fafc;
  font-size: 13px;
  text-align: right;
  word-break: break-all;
}

.dark-community-block {
  margin-top: 16px;
}

.dark-community-title {
  color: #e5edf8;
  font-size: 14px;
  font-weight: 700;
  margin-bottom: 10px;
}

.dark-community-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.dark-community-item {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  padding: 10px 12px;
  background: rgba(255,255,255,0.04);
  border: 1px solid rgba(148, 163, 184, 0.12);
  border-radius: 10px;
  color: #e5edf8;
  font-size: 13px;
}

.dark-loading {
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  color: #cbd5e1;
  font-size: 14px;
}

::v-deep .vehicle-full-dialog {
  margin: 0 !important;
}

::v-deep .vehicle-full-dialog .el-dialog__header {
  display: none;
}

::v-deep .vehicle-full-dialog .el-dialog__body {
  padding: 0;
}

::v-deep .search-box .el-input__inner {
  background: rgba(255,255,255,0.06);
  border-color: rgba(148, 163, 184, 0.2);
  color: #e5edf8;
}

::v-deep .search-box .el-input__inner::placeholder {
  color: #64748b;
}

@media screen and (max-width: 1600px) {
  .top-stats {
    grid-template-columns: repeat(3, 1fr);
  }

  .dashboard-grid {
    grid-template-columns: 1fr;
    grid-template-rows: auto;
  }

  .left-panel {
    grid-row: auto;
  }

  .mini-map {
    min-height: 420px;
  }
}
</style>